import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { getCapsulas, getPersonasMant, detenerCapsula } from '../../api/client'
import { hoyLocal } from '../partes/fechas'
import { useReloj } from './useReloj'
import { duracion, hora, rangos, resumir, segundosVivos, enColacion } from './tiempo'
import ListaCapsulas from './ListaCapsulas'
import { usePuedeAsignarPartes } from '../../utils/mantenimiento'

const RANGOS = ['hoy', 'semana', 'mes', 'mesAnterior', 'todo']

// Side panel "¿En qué estuvo trabajando?": one person's capsules over a
// period -- totals, time per parte, and the day-by-day record. A parte opens
// in the Partes section.
export default function PersonaTrabajo({ personaId, onClose }) {
  const { t } = useTranslation('engineering')
  const puedeEditar = usePuedeAsignarPartes()
  const navigate = useNavigate()
  const ahora = useReloj()
  const [persona, setPersona] = useState(null)
  const [rango, setRango] = useState('semana')
  const [caps, setCaps] = useState(null)
  const [recibido, setRecibido] = useState(0)
  const [error, setError] = useState('')

  useEffect(() => {
    getPersonasMant().then(ps => setPersona(ps.find(p => p.id === personaId) || null)).catch(() => {})
  }, [personaId])

  const cargar = useCallback(() => {
    const r = rango === 'todo' ? {} : rangos(hoyLocal())[rango]
    getCapsulas({ persona_id: personaId, ...r })
      .then(cs => { setCaps(cs); setRecibido(Date.now()); setError('') })
      .catch(() => setError(t('capsulas.cargaFallo')))
  }, [personaId, rango, t])
  useEffect(() => { cargar() }, [cargar])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const vivas = (caps || []).map(c => ({ ...c, segundos: segundosVivos(c, recibido, ahora) }))
  const r = resumir(vivas)
  const enCurso = vivas.find(c => c.abierta)
  const esperando = enCurso && enColacion((caps || []).find(c => c.id === enCurso.id), recibido, ahora)
  const irParte = (id) => navigate(`/operations/partes?parte=${id}`)

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/20" onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <aside className="w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
        <div className="px-5 pt-4 pb-3 border-b border-gray-100 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs text-gray-400">{t('capsulas.enQueTrabajo')}</p>
              <h2 className="text-lg font-semibold text-gray-900">{persona?.nombre || '…'}</h2>
              {persona?.cargo && <p className="text-xs text-gray-500">{persona.cargo}</p>}
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
          </div>
          {enCurso && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm flex items-center justify-between gap-3">
              <span className="text-emerald-800">
                {esperando
                  ? <>◐ {t('capsulas.retoma', { hora: hora(enCurso.inicio), numero: enCurso.parte_numero, unidad: enCurso.unidad_codigo || '' })}</>
                  : <>● {t('capsulas.trabajandoEn', { numero: enCurso.parte_numero, unidad: enCurso.unidad_codigo || '' })} ·{' '}
                    <span className="font-semibold">{duracion(enCurso.segundos)}</span></>}
              </span>
              {puedeEditar && (
                <button type="button" onClick={async () => { await detenerCapsula(enCurso.id).catch(() => {}); cargar() }}
                  className="text-xs font-medium text-emerald-900 hover:underline">{t('capsulas.pausar')}</button>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-1">
            {RANGOS.map(k => (
              <button key={k} type="button" onClick={() => setRango(k)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium ${rango === k ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                {t(`capsulas.rangos.${k}`)}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Cifra etiqueta={t('capsulas.total')} valor={duracion(r.total.seg)} fuerte />
            <Cifra etiqueta={t('capsulas.normales')} valor={duracion(r.total.normal)} />
            <Cifra etiqueta={t('capsulas.extras')} valor={duracion(r.total.extra)} ambar={r.total.extra > 0} />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {error && <p className="text-red-500 text-sm">{error}</p>}
          {!caps ? <p className="text-gray-400 text-sm">{t('unidades.loading')}</p> : (
            <>
              {r.porParte.length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('capsulas.porParte')}</h3>
                  <ul className="space-y-1">
                    {r.porParte.map(p => (
                      <li key={p.parte_id}>
                        <button type="button" onClick={() => irParte(p.parte_id)}
                          className="w-full flex items-center gap-3 text-left rounded-lg px-2 py-1.5 hover:bg-gray-50">
                          <span className="text-sm font-semibold text-gray-900 w-12">#{p.numero}</span>
                          <span className="text-xs font-mono text-gray-500 w-12">{p.unidad}</span>
                          <span className="text-sm text-gray-700 flex-1 truncate">{p.nombre}</span>
                          <Barra valor={p.seg} max={r.porParte[0].seg} />
                          <span className="text-sm font-semibold tabular-nums w-20 text-right">{duracion(p.seg)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('capsulas.historial')}</h3>
                <ListaCapsulas caps={caps} mostrar="parte" recibido={recibido} ahora={ahora} onParte={irParte} />
              </section>
            </>
          )}
        </div>
      </aside>
    </div>
  )
}

export function Cifra({ etiqueta, valor, fuerte, ambar }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
      <p className="text-[11px] text-gray-500">{etiqueta}</p>
      <p className={`${fuerte ? 'text-base font-bold' : 'text-sm font-semibold'} ${ambar ? 'text-amber-700' : 'text-gray-900'} tabular-nums`}>{valor}</p>
    </div>
  )
}

export function Barra({ valor, max, extra = 0 }) {
  const w = max ? Math.max(2, Math.round((valor / max) * 100)) : 0
  const we = valor ? Math.round((extra / valor) * w) : 0
  return (
    <span className="w-24 h-2 rounded-full bg-gray-100 overflow-hidden flex shrink-0">
      <span className="h-full bg-emerald-400" style={{ width: `${w - we}%` }} />
      {we > 0 && <span className="h-full bg-amber-400" style={{ width: `${we}%` }} />}
    </span>
  )
}
